import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryTooltip,
  VictoryScatter,
  VictoryLine,
  Point,
} from 'victory';
import { DomainTuple } from 'victory-core';
import moment from 'moment';

import { Bot } from '../../types';
import './index.css';

import {
  formatBalance,
  formatPercentage,
  formatPrice,
} from '../../../../helpers/NumberFormatter';
import ChartOptions from './ChartOptions';
import { colors } from '../../../../colors';
import LoadingIcons from 'react-loading-icons';
import { DataPoint, ProfitChartApiResponse } from '../types';
import { getBotColor } from '../../../Overview/ClusterItem/Charts/helpers';
import { Transaction } from '../../../Overview/types';
import { ComponentDimensions } from '../../../../types/Layout';
import {
  calculateIsColorTooDark,
  lightenColor,
} from '../../../../helpers/colors';
import {
  createXTickValues,
  getFormatUnit,
} from '../../../../helpers/bundleTransactionsToCompactForm';
import { stretchYValueOnSharedDomain } from '../../../../helpers/Chart';
import {
  buildReferenceLineData,
  CONFIG_CHANGE_REFERENCE_LINE,
  ReferenceLinePatterns,
} from '../../../../helpers/Chart/referenceLines';
import { useGetConfigChangedAtQuery } from '../../../../store/services/api';

interface Props {
  bot: Bot;
  data: ProfitChartApiResponse | undefined;
  isLoading: boolean;
  showCurrentOrder: boolean;
  setShowCurrentOrder: React.Dispatch<React.SetStateAction<boolean>>;
  compactTransactions: Transaction[] | undefined;
}

export enum ChartType {
  TotalStableBalance = 'TOTAL_STABLE_VALUE',
  TotalAssetBalance = 'TOTAL_ASSET_VALUE',
  StableBalance = 'STABLE_VALUE',
  AssetBalance = 'ASSET_VALUE',
  NoAlgoritmicProgression = 'NO_ALGORITMIC_PROGRESSION',
}

type Datum = {
  x: number;
  y: number;
  color: string;
  isBuy: boolean | null;
};

const xDomainBuffer = 0.1;

// const ProfitChart: React.FC<Props> = (props: Props) => {
//   const navigate = useNavigate();

//   const { bot, data, isLoading } = props;
//   const { complexParams } = bot;

//   const rootRef = useRef<any>(null);
//   const [rootDimensions, setRootDimensions] = useState<
//     ComponentDimensions | undefined
//   >(undefined);

//   const [showAccumulativeData, setShowAccumulativeData] = useState(false);

//   const [showTotalBalance, setShowTotalBalance] = useState(true);
//   const [showTotalAssetBalance, setShowTotalAssetBalance] = useState(false);
//   const [showNoAlgorithmicProgression, setShowNoAlgorithmicProgression] =
//     useState(true);
//   const [showStableBalance, setShowStableBalance] = useState(false);
//   const [showAssetBalance, setShowAssetBalance] = useState(false);

//   const botAssetColor = useMemo(
//     () => getBotColor(bot.baseAsset),
//     [bot.baseAsset],
//   );

//   const axisStyle = useMemo(
//     () => ({
//       axis: { stroke: 'rgba(64,64,64,0.7)' },
//       grid: {
//         stroke: 'rgba(64,64,64,0.7)',
//       },
//       ticks: { stroke: 'white', size: 2 },
//       tickLabels: { fontSize: 20, padding: 5, fill: 'gray' },
//     }),
//     [],
//   );

//   /** Helpers */
//   const formatToChartDataPoints = useCallback(
//     (dataPoints: Array<DataPoint>) =>
//       dataPoints.map((dataPoint) => ({
//         x: dataPoint.timestamp,
//         y: dataPoint.value,
//         indicator: dataPoint.indicator,
//       })),
//     [],
//   );

//   const extractCustomTransactionIndicatorLines = useCallback(
//     (dataPoints: Array<ChartDataPoint>) => {
//       const lineCharts: Array<Array<ChartDataPoint>> = [];

//       for (let i = 0; i < dataPoints.length; i++) {
//         const dataPoint = dataPoints[i];

//         const isFirst = i === 0;

//         if (!!dataPoint.indicator && !isFirst) {
//           // Push the previous dataPoint and the current one (will become white lined)
//           lineCharts.push([dataPoints[i - 1], dataPoint]);
//         }
//       }

//       return lineCharts;
//     },
//     [],
//   );

//   /** Data */
//   const totalBalanceData: Array<ChartDataPoint> = useMemo(() => {
//     if (!data?.dataPoints?.total?.stable) {
//       return [];
//     }

//     return formatToChartDataPoints(data.dataPoints.total.stable);
//   }, [formatToChartDataPoints, data?.dataPoints?.total?.stable]);

//   const totalAssetBalanceData: Array<ChartDataPoint> = useMemo(() => {
//     if (!data?.dataPoints?.total?.asset) {
//       return [];
//     }

//     return formatToChartDataPoints(data.dataPoints.total.asset);
//   }, [formatToChartDataPoints, data?.dataPoints?.total?.asset]);

//   const totalBalanceCustomTransactionDataLine = useMemo(
//     () => extractCustomTransactionIndicatorLines(totalBalanceData),
//     [extractCustomTransactionIndicatorLines, totalBalanceData],
//   );

//   const noAlgorithmicProgressionData = useMemo(() => {
//     if (!data?.dataPoints?.noAlgorithmicProgression) {
//       return [];
//     }

//     if (
//       !showTotalBalance &&
//       !!data?.dataPoints?.originalNoAlgorithmicProgression
//     ) {
//       return formatToChartDataPoints(
//         data.dataPoints.originalNoAlgorithmicProgression,
//       );
//     }

//     return formatToChartDataPoints(data.dataPoints.noAlgorithmicProgression);
//   }, [
//     data?.dataPoints.noAlgorithmicProgression,
//     data?.dataPoints.originalNoAlgorithmicProgression,
//     showTotalBalance,
//     formatToChartDataPoints,
//   ]);

//   const noAlgorithmicProgressionCustomTransactionDataLine = useMemo(
//     () => extractCustomTransactionIndicatorLines(noAlgorithmicProgressionData),
//     [extractCustomTransactionIndicatorLines, noAlgorithmicProgressionData],
//   );

//   const stableData = useMemo(() => {
//     if (!data?.dataPoints?.stable.all) {
//       return [];
//     }

//     return formatToChartDataPoints(data.dataPoints.stable.all);
//   }, [formatToChartDataPoints, data?.dataPoints?.stable.all]);

//   const stableDataCustomTransactionDataLine = useMemo(
//     () => extractCustomTransactionIndicatorLines(stableData),
//     [extractCustomTransactionIndicatorLines, stableData],
//   );

//   const assetData = useMemo(() => {
//     if (!data?.dataPoints?.asset.all) {
//       return [];
//     }

//     return formatToChartDataPoints(data.dataPoints.asset.all);
//   }, [formatToChartDataPoints, data?.dataPoints?.asset.all]);

//   const assetDataCustomTransactionDataLine = useMemo(
//     () => extractCustomTransactionIndicatorLines(assetData),
//     [extractCustomTransactionIndicatorLines, assetData],
//   );

//   const stableAccumulativeData = useMemo(() => {
//     if (!data?.dataPoints?.stable.accumulative) {
//       return [];
//     }

//     return formatToChartDataPoints(data.dataPoints.stable.accumulative);
//   }, [formatToChartDataPoints, data?.dataPoints?.stable.accumulative]);

//   const stableAccumulativeDatCustomTransactionDataLine = useMemo(
//     () => extractCustomTransactionIndicatorLines(stableAccumulativeData),
//     [extractCustomTransactionIndicatorLines, stableAccumulativeData],
//   );

//   const assetAccumulativeData = useMemo(() => {
//     if (!data?.dataPoints?.asset.accumulative) {
//       return [];
//     }

//     return formatToChartDataPoints(data.dataPoints.asset.accumulative);
//   }, [formatToChartDataPoints, data?.dataPoints?.asset.accumulative]);

//   const assetAccumulativeDatCustomTransactionDataLine = useMemo(
//     () => extractCustomTransactionIndicatorLines(assetAccumulativeData),
//     [extractCustomTransactionIndicatorLines, assetAccumulativeData],
//   );

//   /** Gradients */
//   const totalBalanceColors = useMemo(() => {
//     if (!totalBalanceData?.length) {
//       return {
//         gradient: 'url(#positiveTotalBalanceGradient)',
//         stroke: colors.white,
//       };
//     }

//     if (
//       totalBalanceData[totalBalanceData.length - 1].y >= totalBalanceData[0].y
//     ) {
//       return {
//         gradient: 'url(#positiveTotalBalanceGradient)',
//         stroke: colors.green,
//       };
//     }

//     return {
//       gradient: 'url(#negativeTotalBalanceGradient)',
//       stroke: colors.red,
//     };
//   }, [totalBalanceData]);

//   const totalAssetBalanceColors = useMemo(() => {
//     return {
//       gradient: 'url(#totalAssetBalanceGradient)',
//       stroke: botAssetColor,
//     };
//   }, [botAssetColor]);

//   const customTransactionsColors = useMemo(() => {
//     return {
//       stroke: colors.white,
//     };
//   }, []);

//   const noAlgorithmicProgressionColors = useMemo(() => {
//     if (!noAlgorithmicProgressionData?.length) {
//       return {
//         gradient: 'url(#positiveNoAlgorithmicProgressionGradient)',
//         stroke: colors.white,
//       };
//     }

//     if (
//       noAlgorithmicProgressionData[noAlgorithmicProgressionData.length - 1].y >=
//       noAlgorithmicProgressionData[0].y
//     ) {
//       return {
//         gradient: 'url(#positiveNoAlgorithmicProgressionGradient)',
//         stroke: colors.mossGreen,
//       };
//     }

//     return {
//       gradient: 'url(#negativeNoAlgorithmicProgressionGradient)',
//       stroke: colors.orange,
//     };
//   }, [noAlgorithmicProgressionData]);

//   const stableBalanceColor = useMemo(() => {
//     return {
//       gradient: 'url(#stableBalanceGradient)',
//       stroke: colors.tetherGreen,
//     };
//   }, []);

//   const assetBalanceColor = useMemo(() => {
//     return {
//       gradient: 'url(#assetBalanceGradient)',
//       stroke: colors.bitcoinOrange,
//     };
//   }, []);

//   const canShowRightAxis = useMemo(
//     () =>
//       (showTotalAssetBalance || showAssetBalance) &&
//       (showNoAlgorithmicProgression || showTotalBalance || showStableBalance),
//     [
//       showAssetBalance,
//       showNoAlgorithmicProgression,
//       showStableBalance,
//       showTotalAssetBalance,
//       showTotalBalance,
//     ],
//   );

//   const domain = useMemo(() => {
//     if (!totalBalanceData.length) {
//       return undefined;
//     }

//     const data = [
//       ...(showTotalBalance ? totalBalanceData : []),
//       ...(showTotalAssetBalance ? totalAssetBalanceData : []),
//       ...(showNoAlgorithmicProgression ? noAlgorithmicProgressionData : []),
//       ...(showStableBalance && !showAccumulativeData ? stableData : []),
//       ...(showStableBalance && showAccumulativeData
//         ? stableAccumulativeData
//         : []),
//       ...(showAssetBalance && !canShowRightAxis && !showAccumulativeData
//         ? assetData
//         : []),
//       ...(showAssetBalance && !canShowRightAxis && showAccumulativeData
//         ? assetAccumulativeData
//         : []),
//     ];

//     if (!data.length) {
//       return undefined;
//     }

//     const minima = minBy<any>(data, (d) => d.y)?.y ?? 0;
//     const maxima = maxBy<any>(data, (d) => d.y)?.y ?? 0;
//     const minimaScaled = minima / (maxima || 1);

//     return {
//       x: [data[0].x, data[data.length - 1].x] as DomainTuple,
//       y: canShowRightAxis
//         ? ([
//             minimaScaled * (minimaScaled > 0 ? 0.95 : 1.05),
//             1.05,
//           ] as DomainTuple)
//         : ([minima * (minima > 0 ? 0.95 : 1.05), maxima * 1.05] as DomainTuple),
//     };
//   }, [
//     totalBalanceData,
//     showTotalBalance,
//     showTotalAssetBalance,
//     totalAssetBalanceData,
//     showNoAlgorithmicProgression,
//     noAlgorithmicProgressionData,
//     showStableBalance,
//     showAccumulativeData,
//     stableData,
//     stableAccumulativeData,
//     showAssetBalance,
//     canShowRightAxis,
//     assetData,
//     assetAccumulativeData,
//   ]);

//   const maxima = useMemo(() => {
//     const data = {
//       totalBalanceData,
//       totalAssetBalanceData,
//       noAlgorithmicProgressionData,
//       stableData,
//       stableAccumulativeData,
//       assetData,
//       assetAccumulativeData,
//     };

//     return Object.entries(data).reduce((acc, [key, dataset]) => {
//       acc[key] = Math.max(...dataset.map((d) => d.y));
//       return acc;
//     }, {} as Record<string, any>);
//   }, [
//     assetAccumulativeData,
//     assetData,
//     noAlgorithmicProgressionData,
//     stableAccumulativeData,
//     stableData,
//     totalAssetBalanceData,
//     totalBalanceData,
//   ]);

//   const calculateTickValue = useCallback((t: Date) => {
//     return moment(t).format('DD MMM');
//   }, []);

//   const getLeftAxisMaxima = useCallback(() => {
//     if (!canShowRightAxis) {
//       return 1;
//     }
//     if (canShowRightAxis || !showAssetBalance) {
//       // We need two y axis, so left one well be stable value
//       return Math.max(
//         maxima.totalBalanceData,
//         maxima.stableData,
//         maxima.noAlgorithmicProgressionData,
//       );
//     }

//     /** We need one y axis, the asset balance */
//     return maxima.totalBalanceData;
//   }, [
//     canShowRightAxis,
//     maxima.noAlgorithmicProgressionData,
//     maxima.stableData,
//     maxima.totalBalanceData,
//     showAssetBalance,
//   ]);

//   const getRightAxisMaxima = useCallback(() => {
//     return showTotalAssetBalance
//       ? maxima.totalAssetBalanceData
//       : maxima.assetData;
//   }, [maxima.assetData, maxima.totalAssetBalanceData, showTotalAssetBalance]);

//   const yAccessor = useCallback(
//     (key: string, datum: { x: number; y: number }) => {
//       const isAssetOnRightAxis =
//         canShowRightAxis &&
//         (key === 'assetAccumulativeData' || key === 'assetData');

//       let addedValue = 0;

//       if (isAssetOnRightAxis) {
//         const diff = 1 - datum.y / maxima[key];
//         const minDomLeft = domain?.y[0] as number;
//         addedValue = minDomLeft * diff;
//       }

//       return (
//         datum.y /
//           (canShowRightAxis
//             ? key === 'assetAccumulativeData' || key === 'assetData'
//               ? maxima[key]
//               : getLeftAxisMaxima()
//             : 1) +
//         (isAssetOnRightAxis ? addedValue : 0)
//       );
//     },
//     [canShowRightAxis, domain?.y, getLeftAxisMaxima, maxima],
//   );

//   const tickValues = useMemo(() => {
//     const minDomLeft = domain?.y[0] as number;
//     const diff = 1 - minDomLeft;
//     const interval = diff / 5;
//     return [
//       minDomLeft + interval,
//       minDomLeft + interval * 2,
//       minDomLeft + interval * 3,
//       minDomLeft + interval * 4,
//       1,
//     ];
//   }, [domain?.y]);

//   const rightAxisTickFormat = useCallback(
//     (t: number) => {
//       const minDomLeft = domain?.y[0] as number;
//       const domDiff = 1 - minDomLeft;
//       const minDomDiff = t - minDomLeft;
//       const diff = minDomDiff / domDiff;
//       const value = diff * getRightAxisMaxima();

//       return formatNumber(value, complexParams?.maxDecimals ?? 0);
//     },
//     [complexParams?.maxDecimals, domain?.y, getRightAxisMaxima],
//   );

//   const handleChartPress = useCallback(() => {
//     navigate('/profitCalculations', {
//       state: {
//         tab: Tabs.SingleProfitCalculation,
//         baseAsset: bot.baseAsset,
//         quoteAsset: bot.quoteAsset,
//         startDate: bot.dateCreated,
//         timeFrames: bot.timeFrame,
//         complexParams: bot.complexParams,
//       },
//     });
//   }, [
//     bot.baseAsset,
//     bot.complexParams,
//     bot.dateCreated,
//     bot.quoteAsset,
//     bot.timeFrame,
//     navigate,
//   ]);

//   useEffect(() => {
//     if (!!rootDimensions) {
//       return;
//     }

//     if (!rootRef.current) {
//       return;
//     }

//     setRootDimensions({
//       height: rootRef.current.clientHeight * 0.6,
//       width: rootRef.current.clientWidth,
//     });
//   }, [rootDimensions]);

//   const monthlyProfitDataSets = useMemo(() => {
//     /**
//      * We start at the beginning of the stableData
//      * For the first one, we go until the end of the month
//      * For the rest we beginning of month until the end
//      * for the last one, beginning of month until now
//      */
//     const data = showTotalBalance ? totalBalanceData : totalAssetBalanceData;

//     if (!data?.length) {
//       return [];
//     }

//     let endTimestamp = Math.min(
//       new Date(
//         new Date(data[0].x).getFullYear(),
//         new Date(data[0].x).getMonth() + 1,
//         0,
//       ).valueOf(),
//       new Date().valueOf(),
//     );

//     let startBalance = data[0].y;
//     let previousBalance = data[0].y;
//     const dataSets: Array<{
//       data: Array<{
//         x: number;
//         y: number;
//         y0?: number;
//         color: string;
//         date?: Date;
//       }>;
//       color?: string;
//     }> = [];

//     for (let i = 0; i < data.length; i++) {
//       const datum = data[i];
//       const profit = (datum.y - startBalance) / startBalance;

//       if (datum.x <= endTimestamp) {
//         if (!dataSets.length) {
//           dataSets.push({ data: [] });
//         }

//         dataSets[dataSets.length - 1].data.push({
//           x: datum.x,
//           date: new Date(datum.x),
//           y: Math.abs(profit),
//           color:
//             profit >= 0
//               ? 'url(#positiveMonthGradient)'
//               : 'url(#negativeMonthGradient)',
//         });
//         previousBalance = datum.y;
//       } else {
//         endTimestamp = new Date(
//           new Date(datum.x).getFullYear(),
//           new Date(datum.x).getMonth() + 1,
//           0,
//         ).valueOf();
//         startBalance = previousBalance;

//         if (dataSets[dataSets.length - 1].data.length) {
//           dataSets.push({
//             data: [
//               {
//                 x: new Date(
//                   new Date(datum.x).getFullYear(),
//                   new Date(datum.x).getMonth(),
//                   0,
//                 ).valueOf(),
//                 date: new Date(
//                   new Date(datum.x).getFullYear(),
//                   new Date(datum.x).getMonth(),
//                   0,
//                 ),
//                 y: Math.abs(profit),
//                 color:
//                   profit >= 0
//                     ? 'url(#positiveMonthGradient)'
//                     : 'url(#negativeMonthGradient)',
//               },
//             ],
//           });
//         }
//       }
//     }

//     if (!domain) {
//       return dataSets;
//     }

//     /** Update the y value to stretch the first 30% of the y-axis */

//     const max = Math.max(
//       ...dataSets.map(({ data }) => Math.max(...data.map((datum) => datum.y))),
//     );

//     dataSets.forEach((dataSet) => {
//       const { data } = dataSet;

//       data.forEach((datum) => {
//         const currentY =
//           Math.max(0, domain.y[0] as number) +
//           (datum.y / max) *
//             ((domain.y[1] as number) - Math.max(0, domain.y[0] as number)) *
//             0.1;

//         datum.y = currentY;
//       });

//       if (data.length) {
//         const lastDatum = data[data.length - 1];
//         dataSet.color = lastDatum.color;
//       }
//     });

//     return dataSets;
//   }, [domain, showTotalBalance, totalAssetBalanceData, totalBalanceData]);

//   return (
//     <div className="prc-container" ref={rootRef}>
//       <svg style={{ position: 'absolute' }}>
//         <defs>
//           <linearGradient
//             id="positiveTotalBalanceGradient"
//             x1="0%"
//             y1="0%"
//             x2="0%"
//             y2="100%"
//           >
//             <stop offset="0%" stopColor={colors.green} stopOpacity={0.4} />
//             <stop offset="100%" stopColor={colors.green} stopOpacity={0.08} />
//           </linearGradient>
//           <linearGradient
//             id="negativeTotalBalanceGradient"
//             x1="0%"
//             y1="0%"
//             x2="0%"
//             y2="100%"
//           >
//             <stop offset="0%" stopColor={colors.red} stopOpacity={0.4} />
//             <stop offset="100%" stopColor={colors.red} stopOpacity={0.08} />
//           </linearGradient>
//           <linearGradient
//             id="positiveNoAlgorithmicProgressionGradient"
//             x1="0%"
//             y1="0%"
//             x2="0%"
//             y2="100%"
//           >
//             <stop offset="0%" stopColor={colors.mossGreen} stopOpacity={0.3} />
//             <stop
//               offset="100%"
//               stopColor={colors.mossGreen}
//               stopOpacity={0.04}
//             />
//           </linearGradient>
//           <linearGradient
//             id="totalAssetBalanceGradient"
//             x1="0%"
//             y1="0%"
//             x2="0%"
//             y2="100%"
//           >
//             <stop offset="0%" stopColor={botAssetColor} stopOpacity={0.9} />
//             <stop offset="100%" stopColor={botAssetColor} stopOpacity={0.04} />
//           </linearGradient>

//           <linearGradient
//             id="negativeNoAlgorithmicProgressionGradientGradient"
//             x1="0%"
//             y1="0%"
//             x2="0%"
//             y2="100%"
//           >
//             <stop offset="0%" stopColor={colors.orange} stopOpacity={0.3} />
//             <stop offset="100%" stopColor={colors.orange} stopOpacity={0.04} />
//           </linearGradient>
//           <linearGradient
//             id="stableBalanceGradient"
//             x1="0%"
//             y1="0%"
//             x2="0%"
//             y2="100%"
//           >
//             <stop
//               offset="0%"
//               stopColor={colors.tetherGreen}
//               stopOpacity={0.4}
//             />
//             <stop
//               offset="100%"
//               stopColor={colors.tetherGreen}
//               stopOpacity={0.08}
//             />
//           </linearGradient>
//           <linearGradient
//             id="assetBalanceGradient"
//             x1="0%"
//             y1="0%"
//             x2="0%"
//             y2="100%"
//           >
//             <stop
//               offset="0%"
//               stopColor={colors.bitcoinOrange}
//               stopOpacity={0.4}
//             />
//             <stop
//               offset="100%"
//               stopColor={colors.bitcoinOrange}
//               stopOpacity={0.08}
//             />
//           </linearGradient>

//           <linearGradient
//             id="positiveMonthGradient"
//             x1="0%"
//             y1="0%"
//             x2="0%"
//             y2="100%"
//           >
//             <stop offset="0%" stopColor={colors.green} stopOpacity={0.3} />
//             <stop offset="100%" stopColor={colors.green} stopOpacity={0.08} />
//           </linearGradient>
//           <linearGradient
//             id="negativeMonthGradient"
//             x1="0%"
//             y1="0%"
//             x2="0%"
//             y2="100%"
//           >
//             <stop offset="0%" stopColor={colors.red} stopOpacity={0.3} />
//             <stop offset="100%" stopColor={colors.red} stopOpacity={0.08} />
//           </linearGradient>
//         </defs>
//       </svg>

//       <div className="prc-container-chart" onClick={handleChartPress}>
//         <VictoryChart
//           height={rootDimensions?.height}
//           width={rootDimensions?.width}
//           domain={domain}
//           padding={{ right: 110, top: 50, left: 80, bottom: 50 }}
//         >
//           <VictoryAxis
//             style={axisStyle}
//             fixLabelOverlap
//             tickCount={6}
//             tickFormat={calculateTickValue}
//           />
//           <VictoryAxis
//             dependentAxis
//             orientation="right"
//             style={{
//               ...axisStyle,
//               tickLabels: {
//                 ...axisStyle.tickLabels,
//                 fontSize: 20,
//               },
//             }}
//             // Re-scale ticks by multiplying by correct maxima
//             tickFormat={(t) =>
//               formatBalance(
//                 t * getLeftAxisMaxima(),
//                 !canShowRightAxis && (showAssetBalance || showTotalAssetBalance)
//                   ? false
//                   : true,
//               )
//             }
//             tickValues={canShowRightAxis ? tickValues : undefined}
//             tickLabelComponent={<VictoryLabel dx={5} />}
//           />
//           {canShowRightAxis && (
//             <VictoryAxis
//               dependentAxis
//               orientation="left"
//               style={{
//                 ...axisStyle,
//                 tickLabels: {
//                   ...axisStyle.tickLabels,
//                   fill: '#999',
//                 },
//               }}
//               // Use normalized tickValues (0 - 1)
//               tickValues={canShowRightAxis ? tickValues : undefined}
//               // Re-scale ticks by multiplying by correct maxima
//               tickFormat={rightAxisTickFormat}
//               tickLabelComponent={<VictoryLabel dx={-5} />}
//             />
//           )}
//           {showAccumulativeData && showStableBalance && (
//             <VictoryArea
//               data={stableAccumulativeData}
//               style={{
//                 data: {
//                   fill: stableBalanceColor.gradient,
//                   stroke: stableBalanceColor.stroke,
//                   strokeWidth: 1,
//                 },
//               }}
//               y={(datum) => yAccessor('stableAccumulativeData', datum)}
//             />
//           )}
//           {!showAccumulativeData && showStableBalance && (
//             <VictoryArea
//               data={stableData}
//               style={{
//                 data: {
//                   fill: stableBalanceColor.gradient,
//                   stroke: stableBalanceColor.stroke,
//                   strokeWidth: 1,
//                 },
//               }}
//               y={(datum) => yAccessor('stableData', datum)}
//             />
//           )}
//           {showAccumulativeData && showAssetBalance && (
//             <VictoryArea
//               data={assetAccumulativeData}
//               style={{
//                 data: {
//                   fill: assetBalanceColor.gradient,
//                   stroke: assetBalanceColor.stroke,
//                   strokeWidth: 1,
//                 },
//               }}
//               y={(datum) => yAccessor('assetAccumulativeData', datum)}
//             />
//           )}
//           {!showAccumulativeData && showAssetBalance && (
//             <VictoryArea
//               data={assetData}
//               style={{
//                 data: {
//                   fill: assetBalanceColor.gradient,
//                   stroke: assetBalanceColor.stroke,
//                   strokeWidth: 1,
//                 },
//               }}
//               y={(datum) => yAccessor('assetData', datum)}
//             />
//           )}
//           {showNoAlgorithmicProgression && (
//             <VictoryArea
//               data={noAlgorithmicProgressionData}
//               style={{
//                 data: {
//                   fill: noAlgorithmicProgressionColors.gradient,
//                   stroke: noAlgorithmicProgressionColors.stroke,
//                   strokeWidth: 1,
//                 },
//               }}
//               y={(datum) => yAccessor('noAlgorithmicProgressionData', datum)}
//             />
//           )}
//           {showTotalAssetBalance && (
//             <VictoryArea
//               data={totalAssetBalanceData}
//               style={{
//                 data: {
//                   fill: totalAssetBalanceColors.gradient,
//                   stroke: totalAssetBalanceColors.stroke,
//                   strokeWidth: 1,
//                 },
//               }}
//               y={(datum) => yAccessor('totalAssetBalanceData', datum)}
//             />
//           )}
//           {showTotalBalance &&
//             totalBalanceCustomTransactionDataLine.map(
//               (customTransactionsDataPoints, index) => (
//                 <VictoryLine
//                   key={`total_balance_custom_${index}`}
//                   data={customTransactionsDataPoints}
//                   style={{
//                     data: {
//                       stroke: customTransactionsColors.stroke,
//                       strokeWidth: 1,
//                     },
//                   }}
//                   y={(datum) => yAccessor('totalBalanceData', datum)}
//                 />
//               ),
//             )}
//           {showTotalBalance && (
//             <VictoryArea
//               data={totalBalanceData}
//               style={{
//                 data: {
//                   fill: totalBalanceColors.gradient,
//                   stroke: totalBalanceColors.stroke,
//                   strokeWidth: 1,
//                 },
//               }}
//               y={(datum) => yAccessor('totalBalanceData', datum)}
//             />
//           )}

//           {showNoAlgorithmicProgression &&
//             noAlgorithmicProgressionCustomTransactionDataLine.map(
//               (customTransactionsDataPoints, index) => (
//                 <VictoryLine
//                   key={`no_algorithm_progression_${index}`}
//                   data={customTransactionsDataPoints}
//                   style={{
//                     data: {
//                       stroke: customTransactionsColors.stroke,
//                       strokeWidth: 1,
//                     },
//                   }}
//                   y={(datum) =>
//                     yAccessor('noAlgorithmicProgressionData', datum)
//                   }
//                 />
//               ),
//             )}
//           {!showAccumulativeData &&
//             showStableBalance &&
//             stableDataCustomTransactionDataLine.map(
//               (customTransactionsDataPoints, index) => (
//                 <VictoryLine
//                   key={`stable_data_${index}`}
//                   data={customTransactionsDataPoints}
//                   style={{
//                     data: {
//                       stroke: customTransactionsColors.stroke,
//                       strokeWidth: 1,
//                     },
//                   }}
//                   y={(datum) => yAccessor('stableData', datum)}
//                 />
//               ),
//             )}
//           {showAccumulativeData &&
//             showStableBalance &&
//             stableAccumulativeDatCustomTransactionDataLine.map(
//               (customTransactionsDataPoints, index) => (
//                 <VictoryLine
//                   key={`stable_accumulative_data_${index}`}
//                   data={customTransactionsDataPoints}
//                   style={{
//                     data: {
//                       stroke: customTransactionsColors.stroke,
//                       strokeWidth: 1,
//                     },
//                   }}
//                   y={(datum) => yAccessor('stableData', datum)}
//                 />
//               ),
//             )}
//           {!showAccumulativeData &&
//             showAssetBalance &&
//             assetDataCustomTransactionDataLine.map(
//               (customTransactionsDataPoints, index) => (
//                 <VictoryLine
//                   key={`asset_data_${index}`}
//                   data={customTransactionsDataPoints}
//                   style={{
//                     data: {
//                       stroke: customTransactionsColors.stroke,
//                       strokeWidth: 1,
//                     },
//                   }}
//                   y={(datum) => yAccessor('stableData', datum)}
//                 />
//               ),
//             )}
//           {showAccumulativeData &&
//             showAssetBalance &&
//             assetAccumulativeDatCustomTransactionDataLine.map(
//               (customTransactionsDataPoints, index) => (
//                 <VictoryLine
//                   key={`asset_accumulative_data_${index}`}
//                   data={customTransactionsDataPoints}
//                   style={{
//                     data: {
//                       stroke: customTransactionsColors.stroke,
//                       strokeWidth: 1,
//                     },
//                   }}
//                   y={(datum) => yAccessor('stableData', datum)}
//                 />
//               ),
//             )}
//           {(showTotalBalance || showTotalAssetBalance) &&
//             !!monthlyProfitDataSets?.length &&
//             monthlyProfitDataSets.map(
//               ({ data: monthlyProfitDataPoints, color }, index) => (
//                 <VictoryArea
//                   key={`monthly_profit_${index}`}
//                   data={monthlyProfitDataPoints}
//                   style={{
//                     data: {
//                       fill: color,
//                     },
//                   }}
//                 />
//               ),
//             )}
//         </VictoryChart>
//       </div>
//       <ChartOptions
//         showTotalBalance={showTotalBalance}
//         setShowTotalBalance={setShowTotalBalance}
//         showTotalAssetBalance={showTotalAssetBalance}
//         setShowTotalAssetBalance={setShowTotalAssetBalance}
//         showNoAlgorithmicProgression={showNoAlgorithmicProgression}
//         setShowNoAlgorithmicProgression={setShowNoAlgorithmicProgression}
//         showStableBalance={showStableBalance}
//         setShowStableBalance={setShowStableBalance}
//         showAssetBalance={showAssetBalance}
//         setShowAssetBalance={setShowAssetBalance}
//         showAccumulativeData={showAccumulativeData}
//         setShowAccumulativeData={setShowAccumulativeData}
//         totalBalanceColor={totalBalanceColors.stroke}
//         totalAssetBalanceColor={botAssetColor}
//         noAlgorithmicProgressionColors={noAlgorithmicProgressionColors.stroke}
//       />
//       {isLoading && (
//         <div className="prc-loading-container">
//           <LoadingIcons.Grid width={40} />
//         </div>
//       )}
//     </div>
//   );
// };

const ProfitChart = (props: Props) => {
  const { bot, data, compactTransactions, isLoading } = props;

  const [selectedChartTypes, setSelectedChartTypes] = useState([
    ChartType.TotalStableBalance,
    ChartType.NoAlgoritmicProgression,
  ]);
  const [showAccumulativeData, setShowAccumulativeData] = useState(false);

  const [zoomedDomain, setZoomedDomain] = useState<{
    x: DomainTuple;
    y: DomainTuple;
  }>();
  const [zoomedStartIndex, setZoomedStartIndex] = useState<number>();
  const [zoomLevel, setZoomLevel] = useState<number>(1);

  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(undefined);
  const rootRef = useRef<any>(null);

  useEffect(() => {
    if (!rootRef.current) {
      return;
    }

    if (!!rootDimensions) {
      return;
    }

    setRootDimensions({
      height: rootRef.current.clientHeight * 0.38,
      width: rootRef.current.clientWidth * 0.6,
    });
  }, [rootDimensions]);

  const selectedChartTypesAxis = useMemo(() => {
    const stable = selectedChartTypes.find((ct) => ct.includes('STABLE'));
    const asset = selectedChartTypes.find((ct) => ct.includes('ASSET'));
    const nap = selectedChartTypes.find((ct) => ct.includes('NO_'));

    const axis = [];

    if (stable) {
      axis.push(stable);
    }

    if (asset) {
      axis.push(asset);
    }

    if (nap && !stable) {
      axis.push(ChartType.NoAlgoritmicProgression);
    }

    return axis;
  }, [selectedChartTypes]);

  const chartPadding = useMemo(
    () => ({
      left: selectedChartTypesAxis.length > 1 ? 68 : 30,
      top: 10,
      right: 60,
      bottom: 30,
    }),
    [selectedChartTypesAxis.length],
  );

  /** ====================================================   Total stable data points ==================================================== */
  const rawTotalStableDataPoints = useMemo(() => {
    return (data?.dataPoints.total.stable ?? [])
      .map((dataPoint: DataPoint) => ({
        x: dataPoint.timestamp,
        y: dataPoint.value,
      }))
      .filter(
        (d) =>
          !zoomedDomain ||
          ((zoomedDomain.x[0] as number) -
            ((zoomedDomain.x[1] as number) - (zoomedDomain.x[0] as number)) *
              xDomainBuffer <=
            d.x &&
            d.x <=
              (zoomedDomain.x[1] as number) +
                ((zoomedDomain.x[1] as number) -
                  (zoomedDomain.x[0] as number)) *
                  xDomainBuffer),
      );
  }, [data?.dataPoints.total.stable, zoomedDomain]);

  const rawTotalStableDataPointsMax = useMemo(
    () => Math.max(...rawTotalStableDataPoints.map((datum) => datum.y)),
    [rawTotalStableDataPoints],
  );

  const totalStableDataOriginalDomain = useMemo(() => {
    return rawTotalStableDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / rawTotalStableDataPointsMax,
    }));
  }, [rawTotalStableDataPoints, rawTotalStableDataPointsMax]);

  /** ==================================================== N.A.P data points ==================================================== */
  const rawNapDataPoints = useMemo(() => {
    return (data?.dataPoints.noAlgorithmicProgression ?? [])
      .map((dataPoint: DataPoint) => ({
        x: dataPoint.timestamp,
        y: dataPoint.value,
      }))
      .filter(
        (d) =>
          !zoomedDomain ||
          ((zoomedDomain.x[0] as number) -
            ((zoomedDomain.x[1] as number) - (zoomedDomain.x[0] as number)) *
              xDomainBuffer <=
            d.x &&
            d.x <=
              (zoomedDomain.x[1] as number) +
                ((zoomedDomain.x[1] as number) -
                  (zoomedDomain.x[0] as number)) *
                  xDomainBuffer),
      );
  }, [data?.dataPoints.noAlgorithmicProgression, zoomedDomain]);

  const rawNapDataPointsMax = useMemo(
    () => Math.max(...rawNapDataPoints.map((datum) => datum.y)),
    [rawNapDataPoints],
  );

  const napDataOriginalDomain = useMemo(() => {
    return rawNapDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / rawNapDataPointsMax,
    }));
  }, [rawNapDataPoints, rawNapDataPointsMax]);

  const napData = useMemo(() => {
    const min = Math.min(...napDataOriginalDomain.map((d) => d.y));
    const max = Math.max(...napDataOriginalDomain.map((d) => d.y));

    return napDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(datum.y, min, max),
    }));
  }, [napDataOriginalDomain]);

  const totalStableData = useMemo(() => {
    const min = Math.min(...totalStableDataOriginalDomain.map((d) => d.y));
    const max = Math.max(...totalStableDataOriginalDomain.map((d) => d.y));

    return totalStableDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(datum.y, min, max),
    }));
  }, [totalStableDataOriginalDomain]);

  const isPositiveChart = useMemo(() => {
    if (rawTotalStableDataPoints.length < 2) {
      return false;
    }

    return (
      rawTotalStableDataPoints[rawTotalStableDataPoints.length - 1].y -
        rawTotalStableDataPoints[0].y >
      0
    );
  }, [rawTotalStableDataPoints]);

  /** ==================================================== Total asset data points ==================================================== */
  const rawTotalAssetDataPoints = useMemo(() => {
    return (data?.dataPoints.total.asset ?? [])
      .map((dataPoint: DataPoint) => ({
        x: dataPoint.timestamp,
        y: dataPoint.value,
      }))
      .filter(
        (d) =>
          !zoomedDomain ||
          ((zoomedDomain.x[0] as number) -
            ((zoomedDomain.x[1] as number) - (zoomedDomain.x[0] as number)) *
              xDomainBuffer <=
            d.x &&
            d.x <=
              (zoomedDomain.x[1] as number) +
                ((zoomedDomain.x[1] as number) -
                  (zoomedDomain.x[0] as number)) *
                  xDomainBuffer),
      );
  }, [data?.dataPoints.total.asset, zoomedDomain]);

  const rawTotalAssetDataPointsMax = useMemo(
    () => Math.max(...rawTotalAssetDataPoints.map((datum) => datum.y)),
    [rawTotalAssetDataPoints],
  );

  const totalAssetDataOriginalDomain = useMemo(() => {
    return rawTotalAssetDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / rawTotalAssetDataPointsMax,
    }));
  }, [rawTotalAssetDataPoints, rawTotalAssetDataPointsMax]);

  const totalAssetData = useMemo(() => {
    const min = Math.min(...totalAssetDataOriginalDomain.map((d) => d.y));
    const max = Math.max(...totalAssetDataOriginalDomain.map((d) => d.y));

    return totalAssetDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(datum.y, min, max),
    }));
  }, [totalAssetDataOriginalDomain]);

  /** ==================================================== Stable data points ==================================================== */
  const rawStableDataPoints = useMemo(() => {
    return (data?.dataPoints.stable.all ?? [])
      .map((dataPoint: DataPoint) => ({
        x: dataPoint.timestamp,
        y: dataPoint.value,
      }))
      .filter(
        (d) =>
          !zoomedDomain ||
          ((zoomedDomain.x[0] as number) -
            ((zoomedDomain.x[1] as number) - (zoomedDomain.x[0] as number)) *
              xDomainBuffer <=
            d.x &&
            d.x <=
              (zoomedDomain.x[1] as number) +
                ((zoomedDomain.x[1] as number) -
                  (zoomedDomain.x[0] as number)) *
                  xDomainBuffer),
      );
  }, [data?.dataPoints.stable.all, zoomedDomain]);

  const rawStableDataPointsMax = useMemo(
    () => Math.max(...rawStableDataPoints.map((datum) => datum.y)),
    [rawStableDataPoints],
  );

  const stableDataOriginalDomain = useMemo(() => {
    return rawStableDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / rawStableDataPointsMax,
    }));
  }, [rawStableDataPoints, rawStableDataPointsMax]);

  const stableData = useMemo(() => {
    const min = Math.min(...stableDataOriginalDomain.map((d) => d.y));
    const max = Math.max(...stableDataOriginalDomain.map((d) => d.y));

    return stableDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(datum.y, min, max),
    }));
  }, [stableDataOriginalDomain]);

  /** ==================================================== Asset data points ==================================================== */
  const rawAssetDataPoints = useMemo(() => {
    return (data?.dataPoints.asset.all ?? [])
      .map((dataPoint: DataPoint) => ({
        x: dataPoint.timestamp,
        y: dataPoint.value,
      }))
      .filter(
        (d) =>
          !zoomedDomain ||
          ((zoomedDomain.x[0] as number) -
            ((zoomedDomain.x[1] as number) - (zoomedDomain.x[0] as number)) *
              xDomainBuffer <=
            d.x &&
            d.x <=
              (zoomedDomain.x[1] as number) +
                ((zoomedDomain.x[1] as number) -
                  (zoomedDomain.x[0] as number)) *
                  xDomainBuffer),
      );
  }, [data?.dataPoints.asset.all, zoomedDomain]);

  const rawAssetDataPointsMax = useMemo(
    () => Math.max(...rawAssetDataPoints.map((datum) => datum.y)),
    [rawAssetDataPoints],
  );

  const assetDataOriginalDomain = useMemo(() => {
    return rawAssetDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / rawAssetDataPointsMax,
    }));
  }, [rawAssetDataPoints, rawAssetDataPointsMax]);

  const assetData = useMemo(() => {
    const min = Math.min(...assetDataOriginalDomain.map((d) => d.y));
    const max = Math.max(...assetDataOriginalDomain.map((d) => d.y));

    return assetDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(datum.y, min, max),
    }));
  }, [assetDataOriginalDomain]);

  const getTransactionTotalValue = useCallback(
    (transaction: any /*Transaction | CustomTransaction*/) => {
      return (
        transaction.currentStableBalance +
        transaction.currentAssetBalance *
          (transaction.orderPrice ?? transaction.currentPrice)
      );
    },
    [],
  );

  const getTransactionTotalAssetValue = useCallback(
    (transaction: any /*Transaction | CustomTransaction*/) => {
      return (
        transaction.currentStableBalance /
          (transaction.orderPrice ?? transaction.currentPrice) +
        transaction.currentAssetBalance
      );
    },
    [],
  );

  const calculateTransactionDotYValue = useCallback(
    (transaction: Transaction) => {
      let dataSet = [];

      const isChartMorphed =
        selectedChartTypes.includes(ChartType.TotalStableBalance) &&
        selectedChartTypes.includes(ChartType.NoAlgoritmicProgression);

      if (isChartMorphed) {
        dataSet = rawTotalStableDataPoints;
      } else if (selectedChartTypes.includes(ChartType.TotalStableBalance)) {
        dataSet = totalStableData;
      } else if (selectedChartTypes.includes(ChartType.TotalAssetBalance)) {
        dataSet = totalAssetData;
      } else {
        dataSet = napData;
      }

      return dataSet.reduce(
        (acc, curr) => {
          const diffData = Math.abs(
            acc.timestamp - transaction.transactionTimestamp,
          );
          const diffTransaction = Math.abs(
            curr.x - transaction.transactionTimestamp,
          );

          if (diffTransaction < diffData) {
            acc.timestamp = curr.x;
            acc.value = curr.y;
          }

          return acc;
        },
        { timestamp: 0, value: 0 },
      );
    },
    [
      napData,
      rawTotalStableDataPoints,
      selectedChartTypes,
      totalAssetData,
      totalStableData,
    ],
  );

  const generateTooltipLabel = useCallback(
    (index: number, transaction: Transaction) => {
      const formattedDate = moment(transaction.transactionTimestamp).format(
        'DD MMM YYYY',
      );

      const isCustomTransaction = transaction.hasOwnProperty('isDistribution');

      if (isCustomTransaction) {
        const transactionType = transaction.transactionType;
        const isStableBalance = transactionType.indexOf('STABLE') !== -1;
        const stableAmount = formatBalance(transaction.stableQuantity, true);
        const assetAmount = `${formatBalance(
          transaction.assetQuantity,
          false,
        )} ${bot.baseAsset}`;

        const amount = isStableBalance
          ? `${stableAmount} (${assetAmount})`
          : `${assetAmount} (${stableAmount})`;

        return `${index + 1}. ${formattedDate}\n${transactionType}\n${amount}`;
      }

      const isBuy = transaction.orderType === 'BUY';

      const stableBalance = formatBalance(
        getTransactionTotalValue(transaction),
      );
      const assetBalance = `${formatBalance(
        getTransactionTotalAssetValue(transaction),
        false,
      )} ${bot.baseAsset}`;
      const balance = isBuy ? assetBalance : stableBalance;

      const profit = isBuy
        ? formatPercentage(transaction.profit ?? 0)
        : formatPercentage(transaction.profit ?? 0);

      return `${index + 1}. ${formattedDate}\n${balance} | ${profit}`;
    },
    [bot.baseAsset, getTransactionTotalAssetValue, getTransactionTotalValue],
  );

  const originalSelectedDataPoints = useMemo(
    () =>
      !data
        ? []
        : selectedChartTypes.map((chartType) => {
            switch (chartType) {
              case ChartType.TotalStableBalance:
                return data.dataPoints.total.stable.map((d) => ({
                  x: d.timestamp,
                  y: d.value,
                }));

              case ChartType.TotalAssetBalance:
                return data.dataPoints.total.asset.map((d) => ({
                  x: d.timestamp,
                  y: d.value,
                }));

              case ChartType.StableBalance:
                return data.dataPoints.stable.all.map((d) => ({
                  x: d.timestamp,
                  y: d.value,
                }));

              case ChartType.AssetBalance:
                return data.dataPoints.asset.all.map((d) => ({
                  x: d.timestamp,
                  y: d.value,
                }));

              case ChartType.NoAlgoritmicProgression:
                return data.dataPoints.noAlgorithmicProgression.map((d) => ({
                  x: d.timestamp,
                  y: d.value,
                }));

              default:
                return [];
            }
          }),
    [data, selectedChartTypes],
  );

  const selectedDataPointsOriginalDomain = useCallback(
    (chartType: ChartType) => {
      switch (chartType) {
        case ChartType.TotalStableBalance:
          return totalStableDataOriginalDomain;

        case ChartType.TotalAssetBalance:
          return totalAssetDataOriginalDomain;

        case ChartType.StableBalance:
          return stableDataOriginalDomain;

        case ChartType.AssetBalance:
          return stableDataOriginalDomain;

        case ChartType.NoAlgoritmicProgression:
          return napDataOriginalDomain;
      }
    },
    [
      napDataOriginalDomain,
      stableDataOriginalDomain,
      totalAssetDataOriginalDomain,
      totalStableDataOriginalDomain,
    ],
  );

  const selectedDataPoints = useMemo(
    () =>
      selectedChartTypes.map((chartType) => {
        const isChartMorphed =
          selectedChartTypes.includes(ChartType.TotalStableBalance) &&
          selectedChartTypes.includes(ChartType.NoAlgoritmicProgression);

        switch (chartType) {
          case ChartType.TotalStableBalance:
            return isChartMorphed ? rawTotalStableDataPoints : totalStableData;

          case ChartType.TotalAssetBalance:
            return totalAssetData;

          case ChartType.StableBalance:
            return stableData;

          case ChartType.AssetBalance:
            return assetData;

          case ChartType.NoAlgoritmicProgression:
            return isChartMorphed ? rawNapDataPoints : napData;
        }
      }),
    [
      assetData,
      napData,
      rawNapDataPoints,
      rawTotalStableDataPoints,
      selectedChartTypes,
      stableData,
      totalAssetData,
      totalStableData,
    ],
  );

  const originalDomainX = useMemo(() => {
    let minX = new Date().valueOf();
    let maxX = 0;

    originalSelectedDataPoints.forEach((selectedDataPoint) => {
      selectedDataPoint.forEach((datum) => {
        minX = Math.min(minX, datum.x);
        maxX = Math.max(maxX, datum.x);
      });
    });

    return [minX, maxX] as DomainTuple;
  }, [originalSelectedDataPoints]);

  const domain = useMemo(() => {
    if (zoomedDomain) {
      return zoomedDomain;
    }

    const isNapVisible = selectedChartTypes.includes(
      ChartType.NoAlgoritmicProgression,
    );

    let minY = isNapVisible ? Infinity : 1;
    let maxY = 0;
    let minX = new Date().valueOf();
    let maxX = 0;

    selectedDataPoints.forEach((selectedDataPoint) => {
      selectedDataPoint.forEach((datum) => {
        minX = Math.min(minX, datum.x);
        maxX = Math.max(maxX, datum.x);
        minY = Math.min(minY, datum.y);
        maxY = Math.max(maxY, datum.y);
      });
    });

    return {
      x: [minX, maxX] as DomainTuple,
      y: [minY, maxY * 1.03] as DomainTuple,
    };
  }, [selectedChartTypes, selectedDataPoints, zoomedDomain]);

  // The latest config change of the bot trading this baseAsset, drawn inside the visible domain only.
  const { data: configChange } = useGetConfigChangedAtQuery(bot.baseAsset, {
    skip: !bot.baseAsset,
  });
  const configChangeLine = useMemo(() => {
    const timestamp = configChange?.configChangedAt;
    const [start, end] = domain.x as [number, number];
    if (!timestamp || timestamp < start || timestamp > end) {
      return undefined;
    }
    const yTop = domain.y[1] as number;
    return {
      lineData: buildReferenceLineData(timestamp, domain.y[0] as number, yTop),
      labelDatum: {
        x: timestamp,
        y: yTop,
        label: `${CONFIG_CHANGE_REFERENCE_LINE.label} ${moment(
          timestamp,
        ).format('DD MMM')} · ${moment().diff(timestamp, 'days')}d`,
      },
    };
  }, [configChange, domain]);

  /** Transactions Data points */
  const transactionDataPoints = useMemo(() => {
    if (!compactTransactions) {
      return [];
    }

    const shouldShowTransactionDots =
      selectedChartTypes.includes(ChartType.TotalStableBalance) ||
      selectedChartTypes.includes(ChartType.TotalAssetBalance) ||
      selectedChartTypes.includes(ChartType.NoAlgoritmicProgression);

    if (!shouldShowTransactionDots) {
      return [];
    }

    const sortedCompactTransactions = [...compactTransactions].sort(
      (a, b) => a.transactionTimestamp - b.transactionTimestamp,
    );

    return sortedCompactTransactions.map((transaction) => {
      const isCustomTransaction = transaction.hasOwnProperty('isDistribution');
      const isBuy = isCustomTransaction
        ? null
        : transaction.orderType === 'BUY';

      const label = generateTooltipLabel(
        transaction.transactionIndex,
        transaction,
      );
      const color = isCustomTransaction
        ? colors.blue
        : isBuy
        ? colors.green
        : colors.red;

      // For the y value, let's just search for the closest y value of the main dataSet
      // The values are heavily modified and doesn't show the original data.

      const { timestamp, value } = calculateTransactionDotYValue(transaction);

      return {
        x: timestamp,
        y: value,
        isBuy,
        color,
        label,
      };
    });
  }, [
    calculateTransactionDotYValue,
    compactTransactions,
    generateTooltipLabel,
    selectedChartTypes,
  ]);

  const axisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 10, padding: 5, fill: 'gray' },
    }),
    [],
  );

  const botColor = useMemo(() => {
    const color = getBotColor(bot.baseAsset);

    return calculateIsColorTooDark(color) ? lightenColor(color) : color;
  }, [bot.baseAsset]);

  const tickValues = useMemo(() => {
    if (!domain?.x[0]) {
      return [];
    }

    return createXTickValues(domain.x[0] as number);
  }, [domain?.x]);

  const getSpecificMaxima = useCallback(
    (chartType: ChartType) => {
      switch (chartType) {
        case ChartType.TotalStableBalance:
          return rawTotalStableDataPointsMax;

        case ChartType.TotalAssetBalance:
          return rawTotalAssetDataPointsMax;

        case ChartType.StableBalance:
          return rawStableDataPointsMax;

        case ChartType.AssetBalance:
          return rawAssetDataPointsMax;

        case ChartType.NoAlgoritmicProgression:
          return rawNapDataPointsMax;

        default:
          return 0;
      }
    },
    [
      rawAssetDataPointsMax,
      rawNapDataPointsMax,
      rawStableDataPointsMax,
      rawTotalAssetDataPointsMax,
      rawTotalStableDataPointsMax,
    ],
  );

  /** Determine on end - start date */
  const calculateTickValue = useCallback(
    (t: Date) => {
      if (!domain?.x) {
        return moment(t).format('DD MMM');
      }

      const formatUnit = getFormatUnit(
        domain.x[0] as number,
        domain.x[1] as number,
      );

      return moment(t).format(formatUnit);
    },
    [domain?.x],
  );

  const calculateYTickValue = useCallback(
    (axisIndex: number, t: any) => {
      const chartType = selectedChartTypesAxis[axisIndex]; // The first axis
      const isChartMorphed =
        selectedChartTypes.includes(ChartType.TotalStableBalance) &&
        selectedChartTypes.includes(ChartType.NoAlgoritmicProgression);

      if (isChartMorphed) {
        return `${formatPrice(
          t,
          chartType !== ChartType.TotalAssetBalance &&
            chartType !== ChartType.AssetBalance,
          bot.complexParams?.maxDecimals || 2,
        )}`;
      }

      const dataPoints = selectedDataPointsOriginalDomain(chartType);
      const max = getSpecificMaxima(chartType);

      const minO = Math.min(...dataPoints.map((d) => d.y));
      const maxO = Math.max(...dataPoints.map((d) => d.y));

      /** Reverse the stretch to get the original value */
      const y = maxO - (1 - t) * ((maxO - minO) * 1.05);

      return `${formatPrice(
        y * max,
        chartType !== ChartType.TotalAssetBalance &&
          chartType !== ChartType.AssetBalance,
        bot.complexParams?.maxDecimals || 2,
      )}`;
    },
    [
      bot.complexParams?.maxDecimals,
      getSpecificMaxima,
      selectedChartTypes,
      selectedChartTypesAxis,
      selectedDataPointsOriginalDomain,
    ],
  );

  const getChartColor = useCallback(
    (chartType: ChartType) => {
      switch (chartType) {
        case ChartType.TotalStableBalance:
        case ChartType.StableBalance:
          return {
            fill: `url(#${
              isPositiveChart ? 'positiveStableColor' : 'negativeStableColor'
            })`,
            stroke: isPositiveChart ? colors.mossGreen : colors.red,
          };

        case ChartType.TotalAssetBalance:
        case ChartType.AssetBalance:
          return {
            fill: 'url(#botColor)',
            stroke: botColor,
          };

        case ChartType.NoAlgoritmicProgression:
          const isPositive =
            napData?.length > 1
              ? napData[napData.length - 1].y >= napData[0].y
              : true;

          return {
            fill: `url(#${
              isPositive ? 'positiveNapColor' : 'negativeNapColor'
            })`,
            stroke: isPositive ? colors.mossGreen : colors.orange,
          };
      }
    },
    [botColor, isPositiveChart, napData],
  );

  const getYAxisStyle = useCallback(
    (chartType: ChartType) => {
      return {
        axis: { stroke: 'rgba(64,64,64,0.7)' },
        grid: {
          stroke: 'rgba(64,64,64,0.7)',
        },
        ticks: { stroke: 'white', size: 2 },
        tickLabels: {
          fontSize: 10,
          padding: 5,
          fill: getChartColor(chartType).stroke,
        },
      };
    },
    [getChartColor],
  );

  const handleTransactionDotPress = useCallback(
    (
      event: React.SyntheticEvent<any, Event>,
      eventProps: { index: number; data: Datum[]; datum: Datum },
    ) => {
      const { isBuy } = eventProps.datum;
      const isStableValueVisible = selectedChartTypes.includes(
        ChartType.StableBalance,
      );

      const startIndex =
        eventProps.index -
        (isBuy ? (isStableValueVisible ? 0 : 1) : isStableValueVisible ? 1 : 0);
      const endIndex =
        eventProps.index +
        (isBuy ? (isStableValueVisible ? 2 : 1) : isStableValueVisible ? 1 : 2);

      const zoomedTransactionDots = eventProps.data.slice(
        startIndex === -1 ? 0 : startIndex,
        endIndex,
      );

      let minX = domain.x[0] as number;
      const maxX = zoomedTransactionDots[zoomedTransactionDots.length - 1].x;

      if (startIndex !== -1) {
        minX = zoomedTransactionDots[0].x;
      }

      const diffX = maxX - minX;
      const bufferSize = diffX * xDomainBuffer;

      const minDomainX = Math.max(
        minX - bufferSize,
        originalDomainX[0] as number,
      );
      const maxDomainX = Math.min(
        maxX + bufferSize,
        originalDomainX[1] as number,
      );

      const zoomedDomain = {
        x: [minDomainX, maxDomainX] as DomainTuple,
        y: [0, 1] as DomainTuple,
      };

      setZoomedDomain(zoomedDomain);
      setZoomedStartIndex(Math.max(-1, startIndex));
      setZoomLevel(1);

      return null;
    },
    [domain.x, originalDomainX, selectedChartTypes],
  );

  const monthlyProfitDataSets = useMemo(() => {
    /**
     * We start at the beginning of the stableData
     * For the first one, we go until the end of the month
     * For the rest we beginning of month until the end
     * for the last one, beginning of month until now
     */
    const isAssetChartSelected =
      selectedChartTypes.includes(ChartType.AssetBalance) ||
      selectedChartTypes.includes(ChartType.TotalAssetBalance);

    const data = isAssetChartSelected ? totalAssetData : totalStableData;

    if (!data?.length) {
      return [];
    }

    let endTimestamp = Math.min(
      new Date(
        new Date(data[0].x).getFullYear(),
        new Date(data[0].x).getMonth() + 1,
        0,
      ).valueOf(),
      new Date().valueOf(),
    );

    let startBalance = data[0].y;
    let previousBalance = data[0].y;
    const dataSets: Array<{
      data: Array<{
        x: number;
        y: number;
        y0?: number;
        color: string;
        date?: Date;
      }>;
      color?: string;
    }> = [];

    for (let i = 0; i < data.length; i++) {
      const datum = data[i];
      const profit = (datum.y - startBalance) / startBalance;

      if (datum.x <= endTimestamp) {
        if (!dataSets.length) {
          dataSets.push({ data: [] });
        }

        dataSets[dataSets.length - 1].data.push({
          x: datum.x,
          date: new Date(datum.x),
          y: Math.abs(profit),
          color:
            profit >= 0
              ? 'url(#positiveMonthGradient)'
              : 'url(#negativeMonthGradient)',
        });
        previousBalance = datum.y;
      } else {
        if (!dataSets.length) {
          dataSets.push({ data: [] });
        }

        endTimestamp = new Date(
          new Date(datum.x).getFullYear(),
          new Date(datum.x).getMonth() + 1,
          0,
        ).valueOf();
        startBalance = previousBalance;

        if (dataSets[dataSets.length - 1].data.length) {
          dataSets.push({
            data: [
              {
                x: new Date(
                  new Date(datum.x).getFullYear(),
                  new Date(datum.x).getMonth(),
                  0,
                ).valueOf(),
                date: new Date(
                  new Date(datum.x).getFullYear(),
                  new Date(datum.x).getMonth(),
                  0,
                ),
                y: Math.abs(profit),
                color:
                  profit >= 0
                    ? 'url(#positiveMonthGradient)'
                    : 'url(#negativeMonthGradient)',
              },
            ],
          });
        }
      }
    }

    if (!domain) {
      return dataSets;
    }

    /** Update the y value to stretch the first 30% of the y-axis */

    const max = Math.max(
      ...dataSets.map(({ data }) => Math.max(...data.map((datum) => datum.y))),
    );

    const xDomainMin = Math.max(0, domain.y[0] as number);
    const xDomainMax = domain.y[1] as number;

    dataSets.forEach((dataSet) => {
      const { data } = dataSet;

      data.forEach((datum) => {
        const currentY =
          xDomainMin +
          (xDomainMin === 0 ? (xDomainMax - xDomainMin) * 0.03 : 0) +
          (datum.y / max) *
            (xDomainMax - xDomainMin) *
            (xDomainMin === 0 ? 0.15 : 0.1);

        datum.y = currentY;
      });

      if (data.length) {
        const lastDatum = data[data.length - 1];
        dataSet.color = lastDatum.color;
      }
    });

    return dataSets;
  }, [domain, selectedChartTypes, totalAssetData, totalStableData]);

  return (
    <div
      className="profit-chart-root"
      ref={rootRef}
      style={{ position: 'relative', marginTop: -15 }}
    >
      <svg style={{ position: 'absolute' }}>
        <defs>
          <linearGradient
            id="positiveStableColor"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.mossGreen} stopOpacity={0.3} />
            <stop
              offset="100%"
              stopColor={colors.mossGreen}
              stopOpacity={0.04}
            />
          </linearGradient>
          <linearGradient
            id="negativeStableColor"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.red} stopOpacity={0.4} />
            <stop offset="100%" stopColor={colors.red} stopOpacity={0.08} />
          </linearGradient>
          <linearGradient id="botColor" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={botColor} stopOpacity={0.6} />
            <stop offset="100%" stopColor={botColor} stopOpacity={0.04} />
          </linearGradient>
          <linearGradient
            id="positiveNapColor"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.mossGreen} stopOpacity={0.3} />
            <stop
              offset="100%"
              stopColor={colors.mossGreen}
              stopOpacity={0.04}
            />
          </linearGradient>
          <linearGradient
            id="negativeNapColor"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.orange} stopOpacity={0.4} />
            <stop offset="100%" stopColor={colors.orange} stopOpacity={0.08} />
          </linearGradient>
          <linearGradient
            id="positiveMonthGradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.green} stopOpacity={0.3} />
            <stop offset="100%" stopColor={colors.green} stopOpacity={0.08} />
          </linearGradient>
          <linearGradient
            id="negativeMonthGradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.red} stopOpacity={0.3} />
            <stop offset="100%" stopColor={colors.red} stopOpacity={0.08} />
          </linearGradient>
          <ReferenceLinePatterns />
        </defs>
      </svg>

      <VictoryChart
        height={rootDimensions?.height}
        width={rootDimensions?.width}
        scale="linear"
        padding={chartPadding}
        domainPadding={{ y: 5 }}
        domain={domain}
      >
        {selectedChartTypesAxis.map((_, index) => (
          <VictoryAxis
            key={`axis_${selectedChartTypesAxis[index]}`}
            dependentAxis
            style={getYAxisStyle(selectedChartTypesAxis[index])}
            tickCount={6}
            orientation={index === 0 ? 'right' : 'left'}
            tickFormat={(t: number) => calculateYTickValue(index, t)}
          />
        ))}

        <VictoryAxis
          tickFormat={calculateTickValue}
          style={axisStyle}
          tickValues={tickValues}
        />

        {selectedDataPoints.map((selectedDataPoint, index) => (
          <VictoryArea
            key={`chart_${selectedChartTypes[index]}`}
            data={selectedDataPoint}
            style={{
              data: {
                ...getChartColor(selectedChartTypes[index]),
                strokeWidth: 1,
              },
            }}
          />
        ))}

        {!!monthlyProfitDataSets?.length &&
          monthlyProfitDataSets.map(
            ({ data: monthlyProfitDataPoints, color }, index) => (
              <VictoryArea
                key={`monthly_profit_${index}`}
                data={monthlyProfitDataPoints}
                style={{
                  data: {
                    fill: color,
                  },
                }}
              />
            ),
          )}

        {configChangeLine && (
          <VictoryLine
            data={configChangeLine.lineData}
            style={{
              data: {
                stroke: `url(#${CONFIG_CHANGE_REFERENCE_LINE.patternId})`,
                strokeWidth: 5,
                pointerEvents: 'none',
              },
            }}
          />
        )}
        {configChangeLine && (
          <VictoryScatter
            data={[configChangeLine.labelDatum]}
            size={0}
            labels={({ datum }) => datum.label}
            labelComponent={
              <VictoryLabel
                style={{
                  fill: CONFIG_CHANGE_REFERENCE_LINE.color,
                  fontWeight: 'bold',
                  fontSize: 10,
                  opacity: 0.7,
                  pointerEvents: 'none',
                }}
                textAnchor="end"
                dy={8}
                dx={-8}
              />
            }
          />
        )}

        <VictoryScatter
          data={transactionDataPoints}
          labelComponent={
            <VictoryTooltip
              constrainToVisibleArea
              cornerRadius={3}
              orientation={'left'}
              centerOffset={{ x: -10, y: -40 }}
              pointerOrientation="right"
              pointerWidth={20}
              flyoutStyle={{
                stroke: (d) => d.datum.color,
                strokeWidth: 1.5,
                fill: 'rgba(255, 255, 255, 0.8)',
              }}
              flyoutPadding={{ left: 20, right: 20, top: 5, bottom: 5 }}
              labelComponent={
                <VictoryLabel
                  textAnchor="start"
                  dx={(d) => (d.datum.isBuy === null ? -60 : -55)}
                  style={[
                    {
                      fill: 'rgb(0, 0, 0)',
                      fontFamily: 'monospace',
                      fontSize: 10,
                      fontWeight: 800,
                    },
                  ]}
                />
              }
            />
          }
          style={{
            data: {
              fill: ({ datum }) => datum.color,
              stroke: colors.white,
              strokeWidth: 0.5,
            },
          }}
          events={[
            {
              target: 'data',
              eventHandlers: {
                onClick: handleTransactionDotPress,
              },
            },
          ]}
          size={zoomedDomain ? 3 : 2}
          dataComponent={
            <Point className="single-profit-calcs-chart-scatter-dot" />
          }
        />
      </VictoryChart>
      <ChartOptions
        selectedChartTypes={selectedChartTypes}
        setSelectedChartTypes={setSelectedChartTypes}
        showAccumulativeData={showAccumulativeData}
        setShowAccumulativeData={setShowAccumulativeData}
        totalBalanceColor={getChartColor(ChartType.TotalStableBalance).stroke}
        totalAssetBalanceColor={
          getChartColor(ChartType.TotalAssetBalance).stroke
        }
        noAlgorithmicProgressionColors={
          getChartColor(ChartType.NoAlgoritmicProgression).stroke
        }
      />
      {isLoading && (
        <div className="prc-loading-container">
          <LoadingIcons.Grid width={40} />
        </div>
      )}
    </div>
  );
};

export default ProfitChart;
